import { useMemo, useState, useCallback } from 'react';

export interface UseSearchFilterOptions<T> {
  data: T[];
  searchFields: (keyof T | string)[];
}

export interface UseSearchFilterReturn<T> {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
  filteredData: T[];
  hasSearch: boolean;
}

/** Helper to get nested object values like 'patient.firstName' */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Generic hook for client-side text search filtering.
 */
export function useSearchFilter<T extends Record<string, any>>({
  data,
  searchFields,
}: UseSearchFilterOptions<T>): UseSearchFilterReturn<T> {
  const [searchTerm, setSearchTerm] = useState('');

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase();

    return data.filter((item) => {
      return searchFields.some((field) => {
        const value = getNestedValue(item, field as string);
        return value?.toString().toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, searchFields]);

  return {
    searchTerm,
    setSearchTerm,
    clearSearch,
    filteredData,
    hasSearch: searchTerm.trim().length > 0,
  };
}
